import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  MoreHoriz,
  Storage as PostgresIcon,
  Inbox as SqsIcon,
  ViewList as DdbIcon,
  Folder as S3Icon,
  Psychology as LlmIcon,
  Extension as GenericIcon,
  Code as JsonIcon,
  DeleteOutline as DeleteIcon,
  Info as InfoIcon,
  Apps as AppsIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

const TYPE_META = {
  postgres: { label: "Postgres", Icon: PostgresIcon, tint: "#38bdf8" },
  awssqs: { label: "SQS", Icon: SqsIcon, tint: "#f472b6" },
  awsdynamodb: { label: "DynamoDB", Icon: DdbIcon, tint: "#a78bfa" },
  awss3bucket: { label: "S3 Bucket", Icon: S3Icon, tint: "#fbbf24" },
  "llm-gateway": { label: "LLM Gateway", Icon: LlmIcon, tint: "#34d399" },
};

const STATUS_COLORS = {
  ready: { bg: "rgba(34,197,94,0.15)", fg: "#16a34a", dot: "#22c55e" },
  active: { bg: "rgba(34,197,94,0.15)", fg: "#16a34a", dot: "#22c55e" },
  available: { bg: "rgba(34,197,94,0.15)", fg: "#16a34a", dot: "#22c55e" },
  synced: { bg: "rgba(34,197,94,0.15)", fg: "#16a34a", dot: "#22c55e" },
  true: { bg: "rgba(34,197,94,0.15)", fg: "#16a34a", dot: "#22c55e" },
  creating: { bg: "rgba(245,158,11,0.15)", fg: "#b45309", dot: "#f59e0b" },
  pending: { bg: "rgba(245,158,11,0.15)", fg: "#b45309", dot: "#f59e0b" },
  provisioning: { bg: "rgba(245,158,11,0.15)", fg: "#b45309", dot: "#f59e0b" },
  false: { bg: "rgba(239,68,68,0.15)", fg: "#b91c1c", dot: "#ef4444" },
  failed: { bg: "rgba(239,68,68,0.15)", fg: "#b91c1c", dot: "#ef4444" },
  error: { bg: "rgba(239,68,68,0.15)", fg: "#b91c1c", dot: "#ef4444" },
  unknown: { bg: "rgba(148,163,184,0.15)", fg: "#475569", dot: "#94a3b8" },
};

const statusStyle = (raw) => {
  const key = String(raw ?? "unknown").toLowerCase();
  return STATUS_COLORS[key] || STATUS_COLORS.unknown;
};

const formatRelativeTime = (date) => {
  if (!date) return "";
  const now = new Date();
  const diff = now - new Date(date);
  if (Number.isNaN(diff) || diff < 0) return "";
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
};

const readStatus = (record, path) => {
  const s = record?.status || {};
  if (path === "postgres") return s.state;
  if (path === "awsdynamodb") return s.tableStatus;
  if (path === "awss3bucket" || path === "awssqs") {
    const cond = Array.isArray(s.conditions) ? s.conditions[0] : null;
    return cond?.status;
  }
  if (Array.isArray(s.conditions) && s.conditions.length > 0) {
    return s.conditions[0]?.status;
  }
  return "unknown";
};

const readStatusMessage = (record) => {
  const cond = record?.status?.conditions;
  if (Array.isArray(cond) && cond[0]?.message) return cond[0].message;
  return null;
};

const readKeyDetail = (record, path) => {
  const s = record?.status || {};
  const spec = record?.spec || {};
  if (path === "postgres") {
    const cls = spec.dbInstanceClass?.replace("db.t4g.", "") || spec.instanceSize || "";
    const gb = spec.allocatedStorage || spec.diskSize;
    return [cls && `class ${cls}`, gb && `${gb} GB`].filter(Boolean).join(" · ");
  }
  if (path === "awss3bucket") return s.location ? `region ${s.location}` : "";
  if (path === "awssqs") return s.queueURL ? "queue ready" : "";
  if (path === "awsdynamodb") return s.tableStatus ? `table ${s.tableStatus.toLowerCase()}` : "";
  return "";
};

/**
 * Chip strip listing apps bound to a service instance. Rendered inside the
 * card between the name block and the flex spacer, and reused as a compact
 * variant in the List view. Renders nothing when empty (keeps card height
 * unchanged for the common no-bindings case).
 */
export function BoundAppsRow({
  boundApps = [],
  loading = false,
  isDarkMode,
  onOpenApp,
  maxVisible = 4,
  compact = false,
}) {
  if (loading && boundApps.length === 0) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: isDarkMode ? "#64748b" : "#94a3b8",
          fontSize: "0.7rem",
          fontStyle: "italic",
        }}
      >
        Checking bindings…
      </Typography>
    );
  }
  if (!Array.isArray(boundApps) || boundApps.length === 0) return null;

  const visible = boundApps.slice(0, maxVisible);
  const overflow = boundApps.length - visible.length;

  const chipHeight = compact ? 20 : 22;
  const chipFontSize = compact ? "0.65rem" : "0.68rem";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap: "wrap",
        rowGap: 0.5,
      }}
    >
      <AppsIcon
        sx={{
          fontSize: "0.95rem",
          color: isDarkMode ? "#94a3b8" : "#64748b",
          mr: 0.25,
        }}
      />
      {visible.map((app) => {
        const key = app?.uid || app?.name || app?.displayName;
        const label = app?.displayName || app?.name || "app";
        return (
          <Tooltip
            key={key}
            title={app?.name ? `Open ${app.name}` : "Open app"}
            arrow
          >
            <Chip
              size="small"
              label={label}
              onClick={
                onOpenApp
                  ? (e) => {
                      e.stopPropagation();
                      onOpenApp(app);
                    }
                  : undefined
              }
              clickable={Boolean(onOpenApp)}
              sx={{
                height: chipHeight,
                fontSize: chipFontSize,
                fontWeight: 500,
                background: isDarkMode
                  ? "rgba(148,163,184,0.12)"
                  : "rgba(15,23,42,0.05)",
                color: isDarkMode ? "#e2e8f0" : "#334155",
                border: `1px solid ${
                  isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.08)"
                }`,
                cursor: onOpenApp ? "pointer" : "default",
                "&:hover": onOpenApp
                  ? {
                      background: isDarkMode
                        ? "rgba(234,88,12,0.16)"
                        : "rgba(234,88,12,0.08)",
                      borderColor: isDarkMode
                        ? "rgba(253,186,116,0.4)"
                        : "rgba(234,88,12,0.35)",
                      color: isDarkMode ? "#fdba74" : "#9a3412",
                    }
                  : undefined,
              }}
            />
          </Tooltip>
        );
      })}
      {overflow > 0 && (
        <Chip
          size="small"
          label={`+${overflow}`}
          sx={{
            height: chipHeight,
            fontSize: chipFontSize,
            fontWeight: 600,
            background: isDarkMode
              ? "rgba(148,163,184,0.08)"
              : "rgba(15,23,42,0.04)",
            color: isDarkMode ? "#94a3b8" : "#64748b",
            border: `1px dashed ${
              isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.10)"
            }`,
          }}
        />
      )}
    </Box>
  );
}

export function ServiceInstanceCard({
  record,
  path,
  isDarkMode,
  onViewJson,
  onDelete,
  onOpenApp,
  onOpenDetail,
  canDelete,
  boundApps = [],
  boundAppsLoading = false,
}) {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const meta = TYPE_META[path] || { label: path, Icon: GenericIcon, tint: "#94a3b8" };
  const { Icon } = meta;

  const displayName =
    record?.metadata?.displayName || record?.metadata?.name || "(unnamed)";
  const status = readStatus(record, path);
  const statusMsg = readStatusMessage(record);
  const keyDetail = readKeyDetail(record, path);
  const ageLabel = formatRelativeTime(record?.metadata?.creationTimestamp);
  const stStyle = statusStyle(status);
  const isDefault = Boolean(record?.metadata?.labels?.default);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <Card
      component={motion.div}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      sx={{
        borderRadius: "16px",
        p: 2,
        background: isDarkMode
          ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
          : "rgba(255,255,255,0.9)",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"}`,
        boxShadow: isDarkMode
          ? "0 4px 14px rgba(0,0,0,0.35)"
          : "0 1px 3px rgba(15,23,42,0.06)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 168,
        transition:
          "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          borderColor: isDarkMode
            ? "rgba(253,186,116,0.4)"
            : "rgba(234,88,12,0.25)",
          boxShadow: isDarkMode
            ? "0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(234,88,12,0.25)"
            : "0 8px 24px rgba(15,23,42,0.10), 0 0 0 1px rgba(234,88,12,0.18)",
        },
      }}
    >
      {/* Header row: type chip + menu */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          icon={<Icon sx={{ fontSize: "1rem !important", color: `${meta.tint} !important` }} />}
          label={meta.label}
          size="small"
          sx={{
            height: 26,
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            background: isDarkMode
              ? "rgba(255,255,255,0.06)"
              : "rgba(15,23,42,0.05)",
            border: `1px solid ${meta.tint}33`,
            color: isDarkMode ? "#e2e8f0" : "#334155",
            "& .MuiChip-icon": { ml: 0.75 },
          }}
        />
        <Box sx={{ flex: 1 }} />
        {isDefault && (
          <Chip
            label="default"
            size="small"
            sx={{
              height: 22,
              fontSize: "0.68rem",
              fontWeight: 500,
              background: isDarkMode ? "rgba(234,88,12,0.15)" : "rgba(234,88,12,0.08)",
              color: isDarkMode ? "#fdba74" : "#9a3412",
              border: `1px solid ${isDarkMode ? "rgba(253,186,116,0.35)" : "rgba(234,88,12,0.25)"}`,
            }}
          />
        )}
        <IconButton size="small" onClick={handleMenuOpen} aria-label="Actions">
          <MoreHoriz fontSize="small" sx={{ color: theme.palette.text.secondary }} />
        </IconButton>
      </Box>

      {/* Name */}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: 1.3,
            color: isDarkMode ? "#f1f5f9" : "#0f172a",
            wordBreak: "break-word",
          }}
        >
          {displayName}
        </Typography>
        {keyDetail && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.25,
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: "0.75rem",
            }}
          >
            {keyDetail}
          </Typography>
        )}
      </Box>

      <BoundAppsRow
        boundApps={boundApps}
        loading={boundAppsLoading}
        isDarkMode={isDarkMode}
        onOpenApp={onOpenApp}
      />

      <Box sx={{ flex: 1 }} />

      {/* Footer: status pill + age */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: "999px",
            background: stStyle.bg,
            color: stStyle.fg,
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "capitalize",
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: stStyle.dot,
            }}
          />
          {String(status ?? "unknown").toLowerCase()}
        </Box>
        {statusMsg && (
          <Tooltip title={statusMsg} arrow>
            <InfoIcon
              fontSize="small"
              sx={{ color: theme.palette.primary.main, fontSize: "1rem" }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {ageLabel && (
          <Typography
            variant="caption"
            sx={{ color: isDarkMode ? "#64748b" : "#94a3b8", fontSize: "0.7rem" }}
          >
            {ageLabel}
          </Typography>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {onOpenDetail && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onOpenDetail(record, path);
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View details</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onViewJson?.(record);
          }}
        >
          <ListItemIcon>
            <JsonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View JSON</ListItemText>
        </MenuItem>
        {!isDefault && (
          <MenuItem
            disabled={!canDelete}
            onClick={() => {
              handleMenuClose();
              onDelete?.(record);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color={canDelete ? "error" : "disabled"} />
            </ListItemIcon>
            <ListItemText sx={{ color: canDelete ? "error.main" : "text.disabled" }}>
              Delete
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
}

export default ServiceInstanceCard;

/**
 * Compact actions menu (View JSON / Delete) for the List view. Same
 * options as the card's own menu, minus the card wrapper.
 */
export function InstanceActionsMenu({
  record,
  path,
  isDarkMode,
  canDelete,
  onViewJson,
  onDelete,
  onOpenDetail,
}) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const isDefault = Boolean(record?.metadata?.labels?.default);

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        aria-label="Actions"
      >
        <MoreHoriz
          fontSize="small"
          sx={{
            color: isDarkMode
              ? theme.palette.grey[400]
              : theme.palette.text.secondary,
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {onOpenDetail && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onOpenDetail(record, path);
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View details</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onViewJson?.(record);
          }}
        >
          <ListItemIcon>
            <JsonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View JSON</ListItemText>
        </MenuItem>
        {!isDefault && (
          <MenuItem
            disabled={!canDelete}
            onClick={() => {
              setAnchor(null);
              onDelete?.(record);
            }}
          >
            <ListItemIcon>
              <DeleteIcon
                fontSize="small"
                color={canDelete ? "error" : "disabled"}
              />
            </ListItemIcon>
            <ListItemText
              sx={{ color: canDelete ? "error.main" : "text.disabled" }}
            >
              Delete
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

